import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  Row,
  Spinner
} from 'reactstrap';
import TablePageSizeSelect from '../common/TablePageSizeSelect';
import MedDataTable from '../common/MedDataTable';
import useStoredTablePageSize from '../../hooks/useStoredTablePageSize';
import ReactEchartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/lib/echarts';
import 'echarts/lib/chart/bar';
import 'echarts/lib/chart/pie';
import 'echarts/lib/component/grid';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/legend';
import apiBase from '../../apiConfig';
import AppContext from '../../context/Context';
import {
  readSpreadsheetRows,
  SPREADSHEET_ACCEPT,
  SPREADSHEET_MAX_IMPORT_BYTES
} from '../../helpers/spreadsheet';
import { getAuthHeaders } from '../../helpers/utils';
import { hasPermission } from '../../helpers/permissions';

const BASE_ENDPOINT = `${apiBase}/p-admin/talabalar.php`;
const OVERVIEW_ENDPOINT = `${BASE_ENDPOINT}?action=overview`;
const IMPORT_BATCH_LABEL = '1000';

const EMPTY_DATA = {
  stats: {
    total_students: 0,
    active_students: 0,
    archived_students: 0,
    faculty_count: 0,
    group_count: 0,
    passport_count: 0,
    import_batch_size: 1000
  },
  faculties: [],
  groups: [],
  statistics: {
    courses: [],
    nationalities: []
  },
  students: [],
  options: {
    courses: [],
    nationalities: [],
    payment_forms: [],
    genders: []
  },
  templates: {
    excel: [],
    hemis: []
  }
};

const EMPTY_PERMISSIONS = {
  view: false,
  import: false,
  update: false,
  archive: false,
  bulk_archive: false,
  delete: false
};

const EMPTY_FORM = {
  talaba_id: null,
  hemis_id: '',
  familya: '',
  ism: '',
  sharif: '',
  faculty: '',
  group: '',
  course: '',
  nationality: '',
  gender: '',
  speciality: '',
  payment_form: '',
  education_type: '',
  passport_series: '',
  passport_number: '',
  phone: '',
  address: '',
  republic: '',
  region: '',
  district: '',
  birth_date: '',
  image: '',
  comment: '',
  direction_code: '',
  jshshir: ''
};

const createImportState = () => ({
  status: 'idle',
  fileName: '',
  progress: 0,
  processedBatches: 0,
  totalBatches: 0,
  summary: null,
  errors: [],
  archiveMessage: ''
});

const toStudentForm = student => ({
  ...EMPTY_FORM,
  talaba_id: student.talaba_id,
  hemis_id: student.hemis_id || '',
  familya: student.familya || '',
  ism: student.ism || '',
  sharif: student.sharif || '',
  faculty: student.faculty_code || student.faculty || '',
  group: student.group_code || student.group || '',
  course: student.course || '',
  nationality: student.nationality || '',
  gender: student.gender || '',
  speciality: student.speciality || '',
  payment_form: student.payment_form || '',
  education_type: student.education_type || '',
  passport_series: student.passport_series || '',
  passport_number: student.passport_number || '',
  phone: student.phone || '',
  address: student.address || '',
  republic: student.republic || '',
  region: student.region || '',
  district: student.district || '',
  birth_date: student.birth_date || '',
  image: student.image || '',
  comment: student.comment || '',
  direction_code: student.direction_code || '',
  jshshir: student.jshshir || ''
});

const statusMeta = archive => {
  if (archive === 0) {
    return {
      label: 'Faol',
      badgeClass: 'psi-students-status psi-students-status--success',
      buttonLabel: 'Arxivlash',
      buttonColor: 'warning'
    };
  }

  return {
    label: 'Arxivda',
    badgeClass: 'psi-students-status psi-students-status--secondary',
    buttonLabel: 'Qaytarish',
    buttonColor: 'success'
  };
};

const extractErrorMessage = (error, fallback) =>
  (error.response && error.response.data && error.response.data.message) || error.message || fallback;

const chunkRows = (rows, size) => {
  const chunks = [];

  for (let index = 0; index < rows.length; index += size) {
    chunks.push(rows.slice(index, index + size));
  }

  return chunks;
};

const aggregateSummary = (current, next) => ({
  received: (current.received || 0) + (next.received || 0),
  inserted: (current.inserted || 0) + (next.inserted || 0),
  updated: (current.updated || 0) + (next.updated || 0),
  skipped: (current.skipped || 0) + (next.skipped || 0),
  errors: (current.errors || 0) + (next.errors || 0),
  archived_missing: next.archived_missing || current.archived_missing || 0,
  archive_skipped: Boolean(next.archive_skipped || current.archive_skipped)
});

const createImportToken = () => {
  if (window.crypto?.getRandomValues) {
    const values = new Uint32Array(2);
    window.crypto.getRandomValues(values);
    return `students-${Date.now()}-${values[0].toString(36)}${values[1].toString(36)}`;
  }

  return `students-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const truncateScopeName = (value, limit = 8) => {
  const text = (value || '').trim();

  if (!text) {
    return 'Tanlov';
  }

  return text.length > limit ? `${text.slice(0, limit)}...` : text;
};

const isScopeFullyArchived = meta => Number(meta?.active_count || 0) === 0 && Number(meta?.archived_count || 0) > 0;

const getScopePrimaryAction = meta =>
  isScopeFullyArchived(meta)
    ? {
        archiveValue: 0,
        color: 'success',
        icon: 'sync-alt',
        label: 'Arxivdan chiqarish'
      }
    : {
        archiveValue: 1,
        color: 'warning',
        icon: 'archive',
        label: 'Arxivlash'
      };

const StudentManagement = () => {
  const { isDark, user } = useContext(AppContext);
  const [data, setData] = useState(EMPTY_DATA);
  const [permissions, setPermissions] = useState(EMPTY_PERMISSIONS);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [facultyFilter, setFacultyFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [nationalityFilter, setNationalityFilter] = useState('all');
  const [pageSize, setPageSize] = useStoredTablePageSize();
  const [busyAction, setBusyAction] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importState, setImportState] = useState(createImportState());
  const [confirmState, setConfirmState] = useState(null);
  const [facultyActionValue, setFacultyActionValue] = useState('');
  const [groupActionValue, setGroupActionValue] = useState('');

  const authHeaders = useMemo(() => getAuthHeaders(), []);
  const requestConfig = useMemo(
    () => ({
      headers: authHeaders
    }),
    [authHeaders]
  );

  const jsonRequestConfig = useMemo(
    () => ({
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json'
      }
    }),
    [authHeaders]
  );

  const loadOverview = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
    }

    try {
      const response = await axios.get(OVERVIEW_ENDPOINT, requestConfig);

      if (response.data && response.data.success) {
        setData(response.data.data || EMPTY_DATA);
        setPermissions(response.data.permissions || EMPTY_PERMISSIONS);
        return;
      }

      throw new Error((response.data && response.data.message) || "Talabalar bazasi yuklanmadi.");
    } catch (error) {
      setBanner({
        color: 'danger',
        text: extractErrorMessage(error, "Talabalar bazasini yuklashda xatolik yuz berdi.")
      });
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [requestConfig]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    if (!data.faculties.length) {
      return;
    }

    const facultyExists = data.faculties.some(faculty => (faculty.value || faculty.name) === facultyActionValue);

    if (!facultyActionValue || !facultyExists) {
      const firstFaculty = data.faculties.find(faculty => (faculty.value || faculty.name) !== '');
      setFacultyActionValue(firstFaculty ? firstFaculty.value || firstFaculty.name : '');
    }
  }, [data.faculties, facultyActionValue]);

  useEffect(() => {
    if (!data.groups.length) {
      return;
    }

    const groupExists = data.groups.some(group => (group.value || group.name) === groupActionValue);

    if (!groupActionValue || !groupExists) {
      const firstGroup = data.groups.find(group => (group.value || group.name) !== '');
      setGroupActionValue(firstGroup ? firstGroup.value || firstGroup.name : '');
    }
  }, [data.groups, groupActionValue]);

  const filteredStudents = useMemo(() => {
    const query = search.trim().toLowerCase();

    return (data.students || []).filter(student => {
      const studentFacultyValue = student.faculty_code || student.faculty;
      const studentGroupValue = student.group_code || student.group;
      const studentCourseValue = student.course || '';
      const studentNationalityValue = student.nationality || '';

      if (statusFilter === 'active' && student.archive !== 0) {
        return false;
      }

      if (statusFilter === 'archived' && student.archive !== 1) {
        return false;
      }

      if (facultyFilter !== 'all' && studentFacultyValue !== facultyFilter) {
        return false;
      }

      if (courseFilter !== 'all' && studentCourseValue !== courseFilter) {
        return false;
      }

      if (groupFilter !== 'all' && studentGroupValue !== groupFilter) {
        return false;
      }

      if (nationalityFilter !== 'all' && studentNationalityValue !== nationalityFilter) {
        return false;
      }

      if (!query) {
        return true;
      }

      const haystack = [
        student.full_name,
        student.faculty,
        student.group,
        student.course,
        student.nationality,
        student.phone,
        student.passport,
        student.hemis_id,
        student.jshshir,
        String(student.talaba_id)
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [courseFilter, data.students, facultyFilter, groupFilter, nationalityFilter, search, statusFilter]);

  const statCards = useMemo(
    () => [
      {
        title: 'Jami talabalar',
        value: data.stats.total_students,
        note: 'umumiy baza',
        tone: 'primary',
        icon: 'boxes'
      },
      {
        title: 'Faol talabalar',
        value: data.stats.active_students,
        note: 'panelda ko`rinadi',
        tone: 'success',
        icon: 'user-check'
      },
      {
        title: 'Arxivdagilar',
        value: data.stats.archived_students,
        note: 'yashirin yozuvlar',
        tone: 'secondary',
        icon: 'archive'
      },
      {
        title: 'Guruhlar',
        value: data.stats.group_count,
        note: 'boshqariladigan',
        tone: 'info',
        icon: 'stream'
      },
      {
        title: 'Pasport qamrovi',
        value: data.stats.passport_count,
        note: 'to`ldirilganlar',
        tone: 'warning',
        icon: 'id-card'
      }
    ],
    [data.stats]
  );

  const chartTextColor = isDark ? '#cbd5e1' : '#475569';
  const chartGridLineColor = isDark ? 'rgba(125, 211, 252, 0.1)' : 'rgba(148, 163, 184, 0.16)';

  const courseChartOptions = useMemo(() => {
    const rows = (data.statistics?.courses || []).slice(0, 8);

    return {
      animationDuration: 900,
      animationEasing: 'cubicOut',
      color: ['#2563eb'],
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' }
      },
      grid: {
        left: 12,
        right: 12,
        top: 18,
        bottom: 12,
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: rows.map(item => item.value || item.name),
        axisLabel: {
          color: chartTextColor,
          interval: 0
        },
        axisLine: { lineStyle: { color: chartGridLineColor } },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: chartTextColor },
        splitLine: { lineStyle: { color: chartGridLineColor } }
      },
      series: [
        {
          name: 'Faol talabalar',
          type: 'bar',
          barWidth: '44%',
          itemStyle: {
            borderRadius: [8, 8, 0, 0]
          },
          data: rows.map(item => item.active_count || 0)
        }
      ]
    };
  }, [chartGridLineColor, chartTextColor, data.statistics]);

  const nationalityChartOptions = useMemo(() => {
    const rows = (data.statistics?.nationalities || []).slice(0, 8);

    return {
      animationDuration: 1000,
      animationEasing: 'quarticOut',
      color: ['#0ea5e9', '#10b981', '#f59e0b', '#6366f1', '#ef4444', '#14b8a6', '#8b5cf6', '#64748b'],
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} talaba'
      },
      legend: {
        bottom: 0,
        type: 'scroll',
        textStyle: {
          color: chartTextColor,
          fontSize: 11
        }
      },
      series: [
        {
          name: 'Millat',
          type: 'pie',
          radius: ['44%', '72%'],
          center: ['50%', '43%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 8,
            borderColor: isDark ? '#08111f' : '#ffffff',
            borderWidth: 2
          },
          label: {
            color: chartTextColor,
            formatter: '{b}'
          },
          data: rows.map(item => ({
            name: item.name,
            value: item.active_count || 0
          }))
        }
      ]
    };
  }, [chartTextColor, data.statistics, isDark]);

  const selectedGroupMeta = useMemo(
    () => data.groups.find(group => (group.value || group.name) === groupActionValue) || null,
    [data.groups, groupActionValue]
  );
  const selectedFacultyMeta = useMemo(
    () => data.faculties.find(faculty => (faculty.value || faculty.name) === facultyActionValue) || null,
    [data.faculties, facultyActionValue]
  );
  const facultyPrimaryAction = useMemo(() => getScopePrimaryAction(selectedFacultyMeta), [selectedFacultyMeta]);
  const groupPrimaryAction = useMemo(() => getScopePrimaryAction(selectedGroupMeta), [selectedGroupMeta]);
  const resolvedPermissions = useMemo(
    () => ({
      view: Boolean(permissions.view || hasPermission(user, 'padmin.students.view')),
      import: Boolean(permissions.import || hasPermission(user, 'padmin.students.import')),
      update: Boolean(permissions.update || hasPermission(user, 'padmin.students.update')),
      archive: Boolean(permissions.archive || hasPermission(user, 'padmin.students.archive')),
      bulk_archive: Boolean(permissions.bulk_archive || hasPermission(user, 'padmin.students.bulk_archive')),
      delete: Boolean(permissions.delete || hasPermission(user, 'padmin.students.delete'))
    }),
    [permissions, user]
  );
  const canImport = resolvedPermissions.import;
  const canUpdate = resolvedPermissions.update;
  const canArchive = resolvedPermissions.archive;
  const canBulkArchive = resolvedPermissions.bulk_archive;
  const canDelete = resolvedPermissions.delete;
  const hasRowActions = canUpdate || canArchive || canDelete;
  const hasBulkScopeActions = canBulkArchive || canDelete;
  const facultyActionBusy = busyAction.startsWith('faculty-');
  const groupActionBusy = busyAction.startsWith('group-');

  const applyOverviewResponse = useCallback(responseData => {
    if (responseData && responseData.data && responseData.data.students) {
      setData(responseData.data);
    }

    if (responseData && responseData.permissions) {
      setPermissions(responseData.permissions);
    }
  }, []);

  const postAction = useCallback(
    (action, payload) => axios.post(`${BASE_ENDPOINT}?action=${action}`, payload, jsonRequestConfig),
    [jsonRequestConfig]
  );

  const runAction = useCallback(
    async ({ actionKey, action, payload, successFallback }) => {
      setBusyAction(actionKey);
      setBanner(null);

      try {
        const response = await postAction(action, payload);

        if (response.data && response.data.success) {
          applyOverviewResponse(response.data);

          const message = response.data.message || successFallback;
          setBanner({ color: 'success', text: message });
          toast.success(message);
          return true;
        }

        throw new Error((response.data && response.data.message) || "Amalni bajarib bo'lmadi.");
      } catch (error) {
        const message = extractErrorMessage(error, "Amalni bajarishda xatolik yuz berdi.");
        setBanner({ color: 'danger', text: message });
        toast.error(message);
        return false;
      } finally {
        setBusyAction('');
      }
    },
    [applyOverviewResponse, postAction]
  );

  const handleEditOpen = useCallback(student => {
    if (!canUpdate) {
      return;
    }

    setEditForm(toStudentForm(student));
    setEditModalOpen(true);
  }, [canUpdate]);

  const handleEditChange = ({ target }) => {
    const { name, value } = target;
    setEditForm(current => ({
      ...current,
      [name]: value
    }));
  };

  const handleEditSave = async () => {
    if (!canUpdate || !editForm.talaba_id) {
      return;
    }

    const actionKey = `edit-${editForm.talaba_id}`;
    const succeeded = await runAction({
      actionKey,
      action: 'update',
      payload: {
        talaba_id: editForm.talaba_id,
        fields: editForm
      },
      successFallback: "Talaba ma'lumoti yangilandi."
    });

    if (succeeded) {
      setEditModalOpen(false);
    }
  };

  const confirmBusy = Boolean(
    confirmState &&
      confirmState.request &&
      confirmState.request.actionKey &&
      busyAction === confirmState.request.actionKey
  );

  const closeConfirm = useCallback(() => {
    if (confirmBusy) {
      return;
    }

    setConfirmState(null);
  }, [confirmBusy]);

  const handleConfirm = async () => {
    if (!confirmState || confirmBusy) {
      return;
    }

    const succeeded = await runAction(confirmState.request);

    if (succeeded) {
      closeConfirm();
    }
  };

  const openStudentArchiveConfirm = useCallback(student => {
    if (!canArchive) {
      return;
    }

    const nextArchiveValue = student.archive === 1 ? 0 : 1;

    setConfirmState({
      icon: nextArchiveValue === 1 ? 'archive' : 'sync-alt',
      title: nextArchiveValue === 1 ? 'Talabani arxivlash' : 'Talabani qaytarish',
      message:
        nextArchiveValue === 1
          ? `${student.full_name} arxivga o'tkaziladi va default ro'yxatdan yashiriladi.`
          : `${student.full_name} arxivdan qaytariladi va yana default ro'yxatda ko'rinadi.`,
      confirmLabel: nextArchiveValue === 1 ? 'Arxivlash' : 'Qaytarish',
      confirmColor: nextArchiveValue === 1 ? 'warning' : 'success',
      request: {
        actionKey: `archive-${student.talaba_id}`,
        action: 'archive',
        payload: {
          talaba_id: student.talaba_id,
          archive: nextArchiveValue
        },
        successFallback: nextArchiveValue === 1 ? 'Talaba arxivlandi.' : 'Talaba qaytarildi.'
      }
    });
  }, [canArchive]);

  const openDeleteConfirm = useCallback(student => {
    if (!canDelete) {
      return;
    }

    setConfirmState({
      icon: 'trash-alt',
      title: "Talabani o'chirish",
      message: `${student.full_name} yozuvi bazadan o'chiriladi. Bu amalni ortga qaytarib bo'lmaydi.`,
      confirmLabel: "O'chirish",
      confirmColor: 'danger',
      request: {
        actionKey: `delete-${student.talaba_id}`,
        action: 'delete',
        payload: {
          talaba_id: student.talaba_id
        },
        successFallback: "Talaba o'chirildi."
      }
    });
  }, [canDelete]);

  const openScopeArchiveConfirm = useCallback((scope, meta, archiveValue) => {
    if (!canBulkArchive || !meta) {
      return;
    }

    const label = meta.name || meta.value;
    const value = meta.value || meta.name;
    const scopeLabel = scope === 'faculty' ? 'fakultet' : 'guruh';
    const itemCount = meta.total_count || 0;

    setConfirmState({
      icon: archiveValue === 1 ? 'archive' : 'sync-alt',
      title:
        archiveValue === 1
          ? `${scope === 'faculty' ? 'Fakultetni' : 'Guruhni'} arxivlash`
          : `${scope === 'faculty' ? 'Fakultetni' : 'Guruhni'} qaytarish`,
      message:
        archiveValue === 1
          ? `${label} ${scopeLabel}idagi ${itemCount} ta talaba arxivga o'tkaziladi va asosiy paneldan yashiriladi.`
          : `${label} ${scopeLabel}idagi ${itemCount} ta talaba arxivdan qaytariladi va yana panelda ko'rinadi.`,
      confirmLabel: archiveValue === 1 ? "Arxivlash" : 'Qaytarish',
      confirmColor: archiveValue === 1 ? 'warning' : 'success',
      request: {
        actionKey: `${scope}-archive-${archiveValue}-${value}`,
        action: scope === 'faculty' ? 'archive-faculty' : 'archive-group',
        payload: {
          [scope]: value,
          archive: archiveValue
        },
        successFallback:
          archiveValue === 1
            ? `${scope === 'faculty' ? 'Fakultet' : 'Guruh'} arxivlandi.`
            : `${scope === 'faculty' ? 'Fakultet' : 'Guruh'} qaytarildi.`
      }
    });
  }, [canBulkArchive]);

  const openScopeDeleteConfirm = useCallback((scope, meta) => {
    if (!canDelete || !meta) {
      return;
    }

    const label = meta.name || meta.value;
    const value = meta.value || meta.name;
    const scopeLabel = scope === 'faculty' ? 'fakultet' : 'guruh';
    const itemCount = meta.total_count || 0;

    setConfirmState({
      icon: 'trash-alt',
      title: scope === 'faculty' ? "Fakultet bo'yicha o'chirish" : "Guruh bo'yicha o'chirish",
      message: `${label} ${scopeLabel}idagi ${itemCount} ta talaba yozuvi bazadan o'chiriladi. Bu amalni ortga qaytarib bo'lmaydi.`,
      confirmLabel: "O'chirish",
      confirmColor: 'danger',
      request: {
        actionKey: `${scope}-delete-${value}`,
        action: scope === 'faculty' ? 'delete-faculty' : 'delete-group',
        payload: {
          [scope]: value
        },
        successFallback: `${scope === 'faculty' ? 'Fakultet' : 'Guruh'} bo'yicha yozuvlar o'chirildi.`
      }
    });
  }, [canDelete]);

  const resetImportModal = () => {
    setImportFile(null);
    setImportState(createImportState());
  };

  const openImportModal = () => {
    if (!canImport) {
      return;
    }

    resetImportModal();
    setImportModalOpen(true);
  };

  const closeImportModal = () => {
    if (importState.status === 'running') {
      return;
    }

    setImportModalOpen(false);
  };

  const handleImportStart = async () => {
    if (!importFile) {
      toast.error('Excel fayl tanlanmagan.');
      return;
    }

    const batchSize = Number(data.stats.import_batch_size) || Number(IMPORT_BATCH_LABEL);

    setImportState({
      status: 'running',
      fileName: importFile.name,
      progress: 0,
      processedBatches: 0,
      totalBatches: 0,
      summary: null,
      errors: [],
      archiveMessage: ''
    });

    try {
      const parsedRows = await readSpreadsheetRows(importFile);

      if (!parsedRows.length) {
        throw new Error("Excel faylda import qilinadigan satr topilmadi.");
      }

      const batches = chunkRows(parsedRows, batchSize);
      const importToken = createImportToken();
      let combinedSummary = {};
      let combinedErrors = [];
      let archiveMessage = '';

      setImportState(current => ({
        ...current,
        totalBatches: batches.length
      }));

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex += 1) {
        const currentBatch = batches[batchIndex].map((row, rowIndex) => ({
          ...row,
          row_number: batchIndex * batchSize + rowIndex + 2
        }));

        const response = await postAction('import-batch', {
          rows: currentBatch,
          import_token: importToken,
          batch_index: batchIndex + 1,
          total_batches: batches.length,
          sync_missing: true
        });

        if (!response.data || !response.data.success) {
          const responseMessage = response.data && response.data.message;
          const responseErrors = response.data && response.data.data && response.data.data.errors;
          const firstRowError = Array.isArray(responseErrors) && responseErrors.length ? responseErrors[0].message : '';

          throw new Error(responseMessage || firstRowError || "Import batch bajarilmadi.");
        }

        const batchData = response.data.data || {};
        const batchSummary = batchData.summary || {};

        combinedSummary = aggregateSummary(combinedSummary, batchSummary);
        combinedErrors = combinedErrors.concat(batchData.errors || []);
        archiveMessage = batchData.archive_message || archiveMessage;

        const nextProgress = Math.round(((batchIndex + 1) / batches.length) * 100);
        const nextProcessedBatches = batchIndex + 1;
        const nextSummary = combinedSummary;
        const nextErrors = combinedErrors.slice(0, 20);
        const nextArchiveMessage = archiveMessage;

        setImportState(current => ({
          ...current,
          status: 'running',
          progress: nextProgress,
          processedBatches: nextProcessedBatches,
          totalBatches: batches.length,
          summary: nextSummary,
          errors: nextErrors,
          archiveMessage: nextArchiveMessage
        }));
      }

      await loadOverview(true);

      setImportState(current => ({
        ...current,
        status: 'success',
        progress: 100,
        summary: combinedSummary,
        errors: combinedErrors.slice(0, 20),
        archiveMessage
      }));

      if (combinedSummary.errors > 0) {
        toast.warning("Import tugadi, lekin ayrim satrlar xatolik bilan o'tdi.");
      } else {
        toast.success('Excel import muvaffaqiyatli yakunlandi.');
      }
    } catch (error) {
      const message = extractErrorMessage(error, "Excel importda xatolik yuz berdi.");

      setImportState(current => ({
        ...current,
        status: 'error',
        archiveMessage: message
      }));

      toast.error(message);
    }
  };

  const columns = useMemo(
    () => [
      {
        dataField: 'order',
        text: 'T/R',
        sort: true,
        classes: 'align-middle font-weight-semi-bold'
      },
      {
        dataField: 'full_name',
        text: 'Talaba',
        sort: true,
        classes: 'align-middle',
        formatter: (value, row) => (
          <div className="psi-students-person">
            <div className="psi-students-person__avatar">{row.initials}</div>
            <div className="psi-students-person__copy">
              <strong>{value}</strong>
              <span>Talaba ID: {row.talaba_id}</span>
            </div>
          </div>
        )
      },
      {
        dataField: 'hemis_id',
        text: 'HEMIS ID',
        sort: true,
        classes: 'align-middle',
        formatter: value => <span className="psi-students-chip">{value || 'Mavjud emas'}</span>
      },
      {
        dataField: 'faculty',
        text: 'Fakultet',
        sort: true,
        classes: 'align-middle'
      },
      {
        dataField: 'group',
        text: 'Guruh',
        sort: true,
        classes: 'align-middle',
        formatter: value => <span className="psi-students-chip psi-students-chip--group">{value || "Ko'rsatilmagan"}</span>
      },
      {
        dataField: 'course',
        text: 'Kurs',
        sort: true,
        classes: 'align-middle',
        formatter: value => <span className="psi-students-chip psi-students-chip--course">{value || "Ko'rsatilmagan"}</span>
      },
      {
        dataField: 'nationality',
        text: 'Millati',
        sort: true,
        classes: 'align-middle',
        formatter: value => value || "Ko'rsatilmagan"
      },
      {
        dataField: 'passport',
        text: 'Pasport',
        sort: true,
        classes: 'align-middle',
        formatter: value => <span className="psi-students-passport">{value || 'Mavjud emas'}</span>
      },
      {
        dataField: 'phone',
        text: 'Telefon',
        sort: true,
        classes: 'align-middle',
        formatter: value => value || 'Mavjud emas'
      },
      {
        dataField: 'archive',
        text: 'Holat',
        sort: true,
        classes: 'align-middle',
        formatter: value => {
          const meta = statusMeta(value);

          return (
            <Badge pill className={meta.badgeClass}>
              {meta.label}
            </Badge>
          );
        }
      },
      hasRowActions
        ? {
            dataField: 'actions',
            text: 'Boshqaruv',
            classes: 'align-middle',
            headerStyle: () => ({ minWidth: '17rem' }),
            formatter: (_, row) => {
              const meta = statusMeta(row.archive);
              const rowBusy =
                busyAction === `edit-${row.talaba_id}` ||
                busyAction === `archive-${row.talaba_id}` ||
                busyAction === `delete-${row.talaba_id}`;

              return (
                <div className="psi-students-actions">
                  {canUpdate ? (
                    <Button
                      color="light"
                      size="sm"
                      className="psi-students-action psi-students-action--edit"
                      disabled={rowBusy}
                      onClick={() => handleEditOpen(row)}
                    >
                      <FontAwesomeIcon icon="edit" />
                      <span>Tahrirlash</span>
                    </Button>
                  ) : null}
                  {canArchive ? (
                    <Button
                      color="light"
                      size="sm"
                      className={`psi-students-action psi-students-action--archive psi-students-action--${meta.buttonColor}`}
                      disabled={rowBusy}
                      onClick={() => openStudentArchiveConfirm(row)}
                    >
                      {busyAction === `archive-${row.talaba_id}` ? <Spinner size="sm" /> : <FontAwesomeIcon icon={row.archive === 1 ? 'sync-alt' : 'archive'} />}
                      <span>{meta.buttonLabel}</span>
                    </Button>
                  ) : null}
                  {canDelete ? (
                    <Button
                      color="light"
                      size="sm"
                      className="psi-students-action psi-students-action--delete"
                      disabled={rowBusy}
                      onClick={() => openDeleteConfirm(row)}
                    >
                      <FontAwesomeIcon icon="trash-alt" />
                      <span>O'chirish</span>
                    </Button>
                  ) : null}
                </div>
              );
            }
          }
        : null
    ].filter(Boolean),
    [busyAction, canArchive, canDelete, canUpdate, handleEditOpen, hasRowActions, openDeleteConfirm, openStudentArchiveConfirm]
  );

  const rowClasses = row =>
    row.archive === 1 ? 'psi-students-row psi-students-row--archived' : 'psi-students-row';

  return (
    <>
      <div className="psi-students-heading mb-3">
        <div>
          <div className="psi-students-heading__nav">Boshqaruv / Talabalar bazasi</div>
          <h3 className="mb-1">Talabalar boshqaruvi</h3>
          <p className="mb-0">
            {canImport || hasRowActions || canBulkArchive
              ? "React panel orqali HEMIS import, arxiv, tahrirlash va tozalash amallari boshqariladi."
              : "Talabalar bazasi monitoringi va qidiruvi ushbu panel orqali ko'rsatiladi."}
          </p>
        </div>
        <Badge pill className="psi-students-heading__badge">
          Batch: {data.stats.import_batch_size || IMPORT_BATCH_LABEL}
        </Badge>
      </div>

      {banner ? (
        <Alert color={banner.color} className="mb-3">
          {banner.text}
        </Alert>
      ) : null}

      <Card className="border-0 shadow-sm mb-3 psi-students-hero">
        <CardBody className="psi-students-hero__body">
          <div className="psi-students-hero__copy">
            <div className="psi-students-hero__eyebrow">HEMIS / Excel sinxronizatsiya</div>
            <h4>
              {canImport
                ? "Talaba bazasini batch import orqali yangilang, eski yozuvlarni arxivga o'tkazing va panelni to'liq boshqaring."
                : "Talabalar bazasini kuzating, qidiring va mavjud yozuvlar bo'yicha holatni nazorat qiling."}
            </h4>
            <p className="mb-0">
              {canImport
                ? `Excel fayl ${data.stats.import_batch_size || IMPORT_BATCH_LABEL} tadan bo'lib yuboriladi. Faylda borlari yangilanadi, topilmaganlari esa import xatosiz tugasa avtomatik arxivlanadi.`
                : "Rolingiz uchun ochiq bo'lgan boshqaruv bloklarigina ko'rsatiladi, qolganlari menyu va sahifada yashiriladi."}
            </p>
          </div>
          <div className="psi-students-hero__actions">
            {canImport ? (
              <Button color="primary" className="psi-students-hero__button" onClick={openImportModal}>
                <FontAwesomeIcon icon="file-alt" className="mr-2" />
                Excel yuklash
              </Button>
            ) : null}
            <Button color="light" className="psi-students-hero__button psi-students-hero__button--ghost" onClick={() => loadOverview()}>
              <FontAwesomeIcon icon="sync-alt" className={`mr-2 ${loading ? 'fa-spin' : ''}`} />
              Yangilash
            </Button>
          </div>
        </CardBody>
      </Card>

      <Row className="mb-3">
        {statCards.map(card => (
          <Col md="6" xl key={card.title} className="mb-3">
            <Card className={`border-0 shadow-sm h-100 psi-students-stat psi-students-stat--${card.tone}`}>
              <CardBody>
                <div className="psi-students-stat__top">
                  <div className="psi-students-stat__icon">
                    <FontAwesomeIcon icon={card.icon} />
                  </div>
                  <div className="psi-students-stat__note">{card.note}</div>
                </div>
                <div className="psi-students-stat__value">{card.value}</div>
                <div className="psi-students-stat__label">{card.title}</div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      <Row className="mb-3">
        <Col xl="7" className="mb-3">
          <Card className="border-0 shadow-sm h-100 psi-students-chart-card">
            <CardBody>
              <div className="psi-students-chart-card__head">
                <div>
                  <h5>Kurs kesimidagi statistika</h5>
                  <span>Faol talabalar kurslar bo'yicha taqsimoti</span>
                </div>
                <Badge color="light">{data.statistics?.courses?.length || 0} kurs</Badge>
              </div>
              {data.statistics?.courses?.length ? (
                <ReactEchartsCore echarts={echarts} option={courseChartOptions} style={{ height: 280 }} notMerge lazyUpdate />
              ) : (
                <div className="psi-students-chart-empty">Kurs bo'yicha ma'lumot topilmadi.</div>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col xl="5" className="mb-3">
          <Card className="border-0 shadow-sm h-100 psi-students-chart-card">
            <CardBody>
              <div className="psi-students-chart-card__head">
                <div>
                  <h5>Millati kesimidagi statistika</h5>
                  <span>Millatlar bo'yicha faol talabalar ulushi</span>
                </div>
                <Badge color="light">{data.statistics?.nationalities?.length || 0} millat</Badge>
              </div>
              {data.statistics?.nationalities?.length ? (
                <ReactEchartsCore echarts={echarts} option={nationalityChartOptions} style={{ height: 280 }} notMerge lazyUpdate />
              ) : (
                <div className="psi-students-chart-empty">Millat bo'yicha ma'lumot topilmadi.</div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {hasBulkScopeActions ? (
        <Row className="mb-3">
          <Col xl="6" className="mb-3 mb-xl-0">
            <Card className="border-0 shadow-sm psi-students-bulk-card h-100">
              <CardBody className="psi-students-bulk-card__body">
                <div className="psi-students-bulk-card__head">
                  <h5 className="mb-0">Fakultet</h5>
                  <Badge color="light">{data.faculties.length} ta</Badge>
                </div>

                <FormGroup className="mb-2">
                  <Label className="psi-students-label">Fakultet</Label>
                  <Input
                    type="select"
                    value={facultyActionValue}
                    onChange={event => setFacultyActionValue(event.target.value)}
                    className="psi-students-input"
                  >
                    {data.faculties.length ? (
                      (data.faculties || []).map(faculty => (
                        <option key={faculty.value || faculty.name} value={faculty.value || faculty.name}>
                          {faculty.name} ({faculty.active_count}/{faculty.total_count})
                        </option>
                      ))
                    ) : (
                      <option value="">Fakultet topilmadi</option>
                    )}
                  </Input>
                </FormGroup>

                {selectedFacultyMeta ? (
                  <div className="psi-students-group-meta mb-3">
                    <div>
                      <strong title={selectedFacultyMeta.name}>{selectedFacultyMeta.name}</strong>
                      <span>{selectedFacultyMeta.total_count} ta yozuv</span>
                    </div>
                    <div className="psi-students-group-meta__chips">
                      <Badge color="light">{selectedFacultyMeta.active_count} faol</Badge>
                      <Badge color="light">{selectedFacultyMeta.archived_count} arxiv</Badge>
                    </div>
                  </div>
                ) : null}

                <div className="psi-students-bulk-actions">
                  {canBulkArchive ? (
                    <Button
                      color={facultyPrimaryAction.color}
                      className="psi-students-bulk-actions__button"
                      disabled={!selectedFacultyMeta || facultyActionBusy}
                      onClick={() =>
                        openScopeArchiveConfirm('faculty', selectedFacultyMeta, facultyPrimaryAction.archiveValue)
                      }
                    >
                      <FontAwesomeIcon icon={facultyPrimaryAction.icon} className="mr-2" />
                      <span className="psi-students-bulk-actions__copy">
                        <strong title={selectedFacultyMeta?.name}>
                          {truncateScopeName(selectedFacultyMeta?.name || selectedFacultyMeta?.value)}
                        </strong>
                        <small>{facultyPrimaryAction.label}</small>
                      </span>
                    </Button>
                  ) : null}
                  {canDelete ? (
                    <Button
                      color="danger"
                      className="psi-students-bulk-actions__button"
                      disabled={!selectedFacultyMeta || facultyActionBusy}
                      onClick={() => openScopeDeleteConfirm('faculty', selectedFacultyMeta)}
                    >
                      <FontAwesomeIcon icon="trash-alt" className="mr-2" />
                      <span className="psi-students-bulk-actions__copy">
                        <strong title={selectedFacultyMeta?.name}>
                          {truncateScopeName(selectedFacultyMeta?.name || selectedFacultyMeta?.value)}
                        </strong>
                        <small>O'chirish</small>
                      </span>
                    </Button>
                  ) : null}
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl="6">
            <Card className="border-0 shadow-sm psi-students-bulk-card h-100">
              <CardBody className="psi-students-bulk-card__body">
                <div className="psi-students-bulk-card__head">
                  <h5 className="mb-0">Guruh</h5>
                  <Badge color="light">{data.groups.length} ta</Badge>
                </div>

                <FormGroup className="mb-2">
                  <Label className="psi-students-label">Guruh</Label>
                  <Input
                    type="select"
                    value={groupActionValue}
                    onChange={event => setGroupActionValue(event.target.value)}
                    className="psi-students-input"
                  >
                    {data.groups.length ? (
                      (data.groups || []).map(group => (
                        <option key={group.value || group.name} value={group.value || group.name}>
                          {group.name} ({group.active_count}/{group.total_count})
                        </option>
                      ))
                    ) : (
                      <option value="">Guruh topilmadi</option>
                    )}
                  </Input>
                </FormGroup>

                {selectedGroupMeta ? (
                  <div className="psi-students-group-meta mb-3">
                    <div>
                      <strong title={selectedGroupMeta.name}>{selectedGroupMeta.name}</strong>
                      <span>{selectedGroupMeta.total_count} ta yozuv</span>
                    </div>
                    <div className="psi-students-group-meta__chips">
                      <Badge color="light">{selectedGroupMeta.active_count} faol</Badge>
                      <Badge color="light">{selectedGroupMeta.archived_count} arxiv</Badge>
                    </div>
                  </div>
                ) : null}

                <div className="psi-students-bulk-actions">
                  {canBulkArchive ? (
                    <Button
                      color={groupPrimaryAction.color}
                      className="psi-students-bulk-actions__button"
                      disabled={!selectedGroupMeta || groupActionBusy}
                      onClick={() =>
                        openScopeArchiveConfirm('group', selectedGroupMeta, groupPrimaryAction.archiveValue)
                      }
                    >
                      <FontAwesomeIcon icon={groupPrimaryAction.icon} className="mr-2" />
                      <span className="psi-students-bulk-actions__copy">
                        <strong title={selectedGroupMeta?.name}>
                          {truncateScopeName(selectedGroupMeta?.name || selectedGroupMeta?.value)}
                        </strong>
                        <small>{groupPrimaryAction.label}</small>
                      </span>
                    </Button>
                  ) : null}
                  {canDelete ? (
                    <Button
                      color="danger"
                      className="psi-students-bulk-actions__button"
                      disabled={!selectedGroupMeta || groupActionBusy}
                      onClick={() => openScopeDeleteConfirm('group', selectedGroupMeta)}
                    >
                      <FontAwesomeIcon icon="trash-alt" className="mr-2" />
                      <span className="psi-students-bulk-actions__copy">
                        <strong title={selectedGroupMeta?.name}>
                          {truncateScopeName(selectedGroupMeta?.name || selectedGroupMeta?.value)}
                        </strong>
                        <small>O'chirish</small>
                      </span>
                    </Button>
                  ) : null}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      ) : null}

      <Row className="mb-3">
        <Col xl="12">
          <Card className="border-0 shadow-sm psi-students-table-card h-100">
            <CardBody className="psi-students-table-card__body">
              <div className="psi-students-toolbar mb-3">
                <div className="psi-students-toolbar__copy">
                  <h5 className="mb-1">Talabalar ro'yxati</h5>
                  <p className="text-600 mb-0">Asosiy panelda default holatda faqat faol talabalar ko'rsatiladi.</p>
                </div>

                <div className="psi-students-toolbar__controls">
                  <Input
                    value={search}
                    onChange={({ target }) => setSearch(target.value)}
                    placeholder="F.I.O., HEMIS ID, pasport, telefon..."
                    className="psi-students-search"
                  />
                </div>
              </div>

              <div className="psi-students-filters mb-3">
                <Input type="select" value={statusFilter} onChange={({ target }) => setStatusFilter(target.value)} className="psi-students-input">
                  <option value="active">Faol talabalar</option>
                  <option value="archived">Arxivdagilar</option>
                  <option value="all">Barchasi</option>
                </Input>

                <Input type="select" value={facultyFilter} onChange={({ target }) => setFacultyFilter(target.value)} className="psi-students-input">
                  <option value="all">Barcha fakultetlar</option>
                  {(data.faculties || []).map(faculty => (
                    <option key={faculty.value || faculty.name} value={faculty.value || faculty.name}>
                      {faculty.name} ({faculty.total_count})
                    </option>
                  ))}
                </Input>

                <Input type="select" value={courseFilter} onChange={({ target }) => setCourseFilter(target.value)} className="psi-students-input">
                  <option value="all">Barcha kurslar</option>
                  {(data.options.courses || []).map(course => (
                    <option key={course} value={course}>
                      {course}
                    </option>
                  ))}
                </Input>

                <Input type="select" value={groupFilter} onChange={({ target }) => setGroupFilter(target.value)} className="psi-students-input">
                  <option value="all">Barcha guruhlar</option>
                  {(data.groups || []).map(group => (
                    <option key={group.value || group.name} value={group.value || group.name}>
                      {group.name} ({group.total_count})
                    </option>
                  ))}
                </Input>

                <Input type="select" value={nationalityFilter} onChange={({ target }) => setNationalityFilter(target.value)} className="psi-students-input">
                  <option value="all">Barcha millatlar</option>
                  {(data.options.nationalities || []).map(nationality => (
                    <option key={nationality} value={nationality}>
                      {nationality}
                    </option>
                  ))}
                </Input>

                <TablePageSizeSelect value={pageSize} onChange={setPageSize} className="psi-students-page-size" />
              </div>

              <div className="psi-students-meta mb-3">
                <Badge color="light" pill className="px-3 py-2">
                  {filteredStudents.length} ta yozuv
                </Badge>
                <div className="psi-students-meta__legend">
                  <span className="psi-students-meta__item">
                    <strong>{data.stats.active_students}</strong> faol
                  </span>
                  <span className="psi-students-meta__item">
                    <strong>{data.stats.archived_students}</strong> arxiv
                  </span>
                </div>
              </div>

              {loading ? (
                <div className="psi-students-loading">
                  <Spinner color="primary" className="mb-3" />
                  <div>Talabalar bazasi yuklanmoqda...</div>
                </div>
              ) : (
                <div className="psi-students-table-scroll">
                  <MedDataTable
                    data={filteredStudents}
                    columns={columns}
                    keyField="talaba_id"
                    pageSize={pageSize}
                    rowClasses={rowClasses}
                    paginationResetKey={`${search}-${statusFilter}-${facultyFilter}-${courseFilter}-${groupFilter}-${nationalityFilter}`}
                    noDataIndication={<div className="text-center py-5 text-500">Tanlangan filtr bo'yicha talaba topilmadi.</div>}
                    tableClasses="table table-hover fs--1 mb-0 med-dashboard-table psi-students-table"
                    headerClasses="bg-200 text-900"
                    wrapperClasses="table-responsive psi-students-table-wrap"
                  />
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {canUpdate ? (
        <Modal isOpen={editModalOpen} toggle={() => setEditModalOpen(open => !open)} size="xl" className="psi-students-modal">
        <ModalHeader toggle={() => setEditModalOpen(false)}>Talaba ma'lumotini tahrirlash</ModalHeader>
        <ModalBody>
          <Row form>
            <Col md="6">
              <FormGroup>
                <Label className="psi-students-label">HEMIS ID</Label>
                <Input name="hemis_id" value={editForm.hemis_id} onChange={handleEditChange} className="psi-students-input" />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label className="psi-students-label">JSHSHIR</Label>
                <Input name="jshshir" value={editForm.jshshir} onChange={handleEditChange} className="psi-students-input" />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="psi-students-label">Familya</Label>
                <Input name="familya" value={editForm.familya} onChange={handleEditChange} className="psi-students-input" />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="psi-students-label">Ism</Label>
                <Input name="ism" value={editForm.ism} onChange={handleEditChange} className="psi-students-input" />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="psi-students-label">Sharif</Label>
                <Input name="sharif" value={editForm.sharif} onChange={handleEditChange} className="psi-students-input" />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label className="psi-students-label">Fakultet</Label>
                <Input name="faculty" value={editForm.faculty} onChange={handleEditChange} className="psi-students-input" />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label className="psi-students-label">Guruh</Label>
                <Input name="group" value={editForm.group} onChange={handleEditChange} className="psi-students-input" />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="psi-students-label">Kurs</Label>
                <Input name="course" value={editForm.course} onChange={handleEditChange} className="psi-students-input" />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="psi-students-label">Millati</Label>
                <Input name="nationality" value={editForm.nationality} onChange={handleEditChange} className="psi-students-input" />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="psi-students-label">Jinsi</Label>
                <Input type="select" name="gender" value={editForm.gender} onChange={handleEditChange} className="psi-students-input">
                  <option value="">Tanlang</option>
                  {(data.options.genders || []).map(item => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="psi-students-label">Ta'lim turi</Label>
                <Input name="education_type" value={editForm.education_type} onChange={handleEditChange} className="psi-students-input" />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label className="psi-students-label">Mutaxassislik</Label>
                <Input name="speciality" value={editForm.speciality} onChange={handleEditChange} className="psi-students-input" />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label className="psi-students-label">To'lov shakli</Label>
                <Input
                  type="select"
                  name="payment_form"
                  value={editForm.payment_form}
                  onChange={handleEditChange}
                  className="psi-students-input"
                >
                  <option value="">Tanlang</option>
                  {(data.options.payment_forms || []).map(item => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="psi-students-label">Pasport seriya</Label>
                <Input name="passport_series" value={editForm.passport_series} onChange={handleEditChange} className="psi-students-input" />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="psi-students-label">Pasport raqam</Label>
                <Input name="passport_number" value={editForm.passport_number} onChange={handleEditChange} className="psi-students-input" />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="psi-students-label">Telefon</Label>
                <Input name="phone" value={editForm.phone} onChange={handleEditChange} className="psi-students-input" />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="psi-students-label">Tug'ilgan sana</Label>
                <Input name="birth_date" value={editForm.birth_date} onChange={handleEditChange} className="psi-students-input" />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="psi-students-label">Yo'nalish kodi</Label>
                <Input name="direction_code" value={editForm.direction_code} onChange={handleEditChange} className="psi-students-input" />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="psi-students-label">Respublika</Label>
                <Input name="republic" value={editForm.republic} onChange={handleEditChange} className="psi-students-input" />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="psi-students-label">Viloyat</Label>
                <Input name="region" value={editForm.region} onChange={handleEditChange} className="psi-students-input" />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="psi-students-label">Tuman</Label>
                <Input name="district" value={editForm.district} onChange={handleEditChange} className="psi-students-input" />
              </FormGroup>
            </Col>
            <Col md="12">
              <FormGroup>
                <Label className="psi-students-label">Manzil</Label>
                <Input name="address" value={editForm.address} onChange={handleEditChange} className="psi-students-input" />
              </FormGroup>
            </Col>
            <Col md="12">
              <FormGroup>
                <Label className="psi-students-label">Rasm URL</Label>
                <Input name="image" value={editForm.image} onChange={handleEditChange} className="psi-students-input" />
              </FormGroup>
            </Col>
            <Col md="12">
              <FormGroup className="mb-0">
                <Label className="psi-students-label">Izoh</Label>
                <Input
                  type="textarea"
                  rows="4"
                  name="comment"
                  value={editForm.comment}
                  onChange={handleEditChange}
                  className="psi-students-input"
                />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setEditModalOpen(false)}>
            Bekor qilish
          </Button>
          <Button color="primary" onClick={handleEditSave} disabled={busyAction === `edit-${editForm.talaba_id}`}>
            {busyAction === `edit-${editForm.talaba_id}` ? <Spinner size="sm" className="mr-2" /> : <FontAwesomeIcon icon="check" className="mr-2" />}
            Saqlash
          </Button>
        </ModalFooter>
        </Modal>
      ) : null}

      {canImport ? (
        <Modal isOpen={importModalOpen} toggle={closeImportModal} size="lg" className="psi-students-modal">
        <ModalHeader toggle={closeImportModal}>Excel orqali import</ModalHeader>
        <ModalBody>
          <div className="psi-students-import-panel">
            <div className="psi-students-import-panel__copy">
              <strong>Batch import qoidasi</strong>
              <span>
                Fayl birinchi sheet bo'yicha o'qiladi va {data.stats.import_batch_size || IMPORT_BATCH_LABEL} tadan yuboriladi.
              </span>
              <span>Yangilash HEMIS ID bo'yicha ishlaydi. Bazada bu qiymat `pasport` ustunida tekshiriladi.</span>
              <span>Millati ustuni shablonning oxirgi ustuni sifatida qabul qilinadi.</span>
            </div>
            <Input
              type="file"
              accept={SPREADSHEET_ACCEPT}
              disabled={importState.status === 'running'}
              onChange={event => {
                const file = event.target.files && event.target.files[0];
                setImportFile(file || null);
                setImportState(createImportState());
              }}
              className="psi-students-input"
            />
            <small className="text-500 d-block mt-2">
              Xavfsizlik uchun faqat `.xlsx` va `.csv`, maksimal {Math.round(SPREADSHEET_MAX_IMPORT_BYTES / (1024 * 1024))} MB.
            </small>
          </div>

          <div className="psi-students-template-grid">
            <div className="psi-students-template-card">
              <strong>Excel sarlavhalari</strong>
              <div className="psi-students-template-card__chips">
                {(data.templates.excel || []).map(item => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </div>
            <div className="psi-students-template-card">
              <strong>HEMIS sarlavhalari</strong>
              <div className="psi-students-template-card__chips">
                {(data.templates.hemis || []).map(item => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </div>
          </div>

          {importState.status !== 'idle' ? (
            <div className="psi-students-import-result mt-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>{importState.fileName || 'Import jarayoni'}</strong>
                <span>
                  {importState.processedBatches}/{importState.totalBatches || 0} batch
                </span>
              </div>
              <Progress value={importState.progress} className="mb-3" />

              {importState.summary ? (
                <div className="psi-students-import-summary">
                  <div>
                    <strong>{importState.summary.received || 0}</strong>
                    <span>qabul qilindi</span>
                  </div>
                  <div>
                    <strong>{importState.summary.inserted || 0}</strong>
                    <span>qo'shildi</span>
                  </div>
                  <div>
                    <strong>{importState.summary.updated || 0}</strong>
                    <span>yangilandi</span>
                  </div>
                  <div>
                    <strong>{importState.summary.skipped || 0}</strong>
                    <span>o'tkazib yuborildi</span>
                  </div>
                  <div>
                    <strong>{importState.summary.errors || 0}</strong>
                    <span>xato</span>
                  </div>
                  <div>
                    <strong>{importState.summary.archived_missing || 0}</strong>
                    <span>arxivlandi</span>
                  </div>
                </div>
              ) : null}

              {importState.archiveMessage ? (
                <Alert color={importState.status === 'error' ? 'danger' : 'warning'} className="mb-3">
                  {importState.archiveMessage}
                </Alert>
              ) : null}

              {importState.errors.length ? (
                <div className="psi-students-import-errors">
                  <strong>Namuna xatolar</strong>
                  <ul className="mb-0">
                    {importState.errors.map((item, index) => (
                      <li key={`${item.row_number || index}-${index}`}>
                        {item.row_number ? `${item.row_number}-satr:` : 'Satr:'} {item.message}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={closeImportModal} disabled={importState.status === 'running'}>
            Yopish
          </Button>
          <Button color="primary" onClick={handleImportStart} disabled={!importFile || importState.status === 'running'}>
            {importState.status === 'running' ? <Spinner size="sm" className="mr-2" /> : <FontAwesomeIcon icon="file-alt" className="mr-2" />}
            Importni boshlash
          </Button>
        </ModalFooter>
        </Modal>
      ) : null}

      <Modal isOpen={Boolean(confirmState)} toggle={closeConfirm} centered className="psi-students-confirm-modal">
        <ModalBody className="text-center p-4 p-md-5">
          <div className="psi-students-confirm-modal__icon">
            <FontAwesomeIcon icon={(confirmState && confirmState.icon) || 'exclamation-triangle'} />
          </div>
          <h4 className="psi-students-confirm-modal__title">{(confirmState && confirmState.title) || 'Tasdiqlash'}</h4>
          <p className="psi-students-confirm-modal__message mb-0">{(confirmState && confirmState.message) || ''}</p>
        </ModalBody>
        <ModalFooter className="border-0 pt-0 px-4 pb-4 d-flex justify-content-center">
          <Button color="light" onClick={closeConfirm} disabled={confirmBusy}>
            Bekor qilish
          </Button>
          <Button color={(confirmState && confirmState.confirmColor) || 'primary'} onClick={handleConfirm} disabled={confirmBusy}>
            {confirmBusy ? <Spinner size="sm" className="mr-2" /> : null}
            {(confirmState && confirmState.confirmLabel) || 'Tasdiqlash'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default StudentManagement;
